import React, { useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, BookOpen, Shield, Heart, Users, Leaf } from 'lucide-react';
import NavBar from '@/components/NavBar';
import { BOOKING_URL } from '@/constants/urls';
import LegalServiceSchema from '@/components/LegalServiceSchema';

const TermsConditionsPage: React.FC = () => {
  useEffect(() => {
    // Update document title and meta tags
    document.title = "Terms & Conditions | Women and The Way - Chinese Medicine";
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', "Terms and conditions for Women and The Way Chinese Medicine clinic in Currimundi, Sunshine Coast. Professional standards, informed consent, and holistic care guidelines.");
    }

    // Update Open Graph tags
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', "Terms & Conditions | Women and The Way");
    }

    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', "Terms and conditions for Women and The Way Chinese Medicine clinic. Professional standards, informed consent, and holistic care guidelines.");
    }

    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) {
      ogUrl.setAttribute('content', "https://womenandtheway.com.au/terms-conditions");
    }

    // Update Twitter Card
    const twitterTitle = document.querySelector('meta[name="twitter:title"]');
    if (twitterTitle) {
      twitterTitle.setAttribute('content', "Terms & Conditions | Women and The Way");
    }

    // Cleanup function
    return () => {
      // Reset title on unmount
      document.title = "Acupuncture Sunshine Coast & Caloundra | Women and The Way - Chinese Medicine";
    };
  }, []);

  const handleBooking = () => {
    window.open(BOOKING_URL, '_blank');
  };

  return (
    <>
      <LegalServiceSchema
        pageType="terms"
        title="Terms & Conditions | Women and The Way"
        description="Terms and conditions for Women and The Way Chinese Medicine clinic in Currimundi, Sunshine Coast. Professional standards, informed consent, and holistic care guidelines."
        url="https://womenandtheway.com.au/terms-conditions"
      />
      <NavBar />
      <div className="min-h-screen bg-warm-beige pt-24 md:pt-32">
        {/* Hero Section */}
        <section className="py-16 px-6 bg-gradient-to-r from-sage-50 via-white to-amber-50">
          <div className="container mx-auto max-w-4xl text-center">
            <div className="mb-8">
              <Shield className="w-12 h-12 text-sage-green mx-auto mb-6" />
              <h1 className="font-serif text-4xl md:text-5xl font-bold text-earth-brown mb-6 leading-tight">
                Terms & Conditions
              </h1>
              <p className="text-xl text-gray-600 font-light">
                Our commitment to professional care, your rights, and the integrity of our services
              </p>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <main className="py-12 px-6">
          <div className="container mx-auto max-w-4xl">
            {/* Introduction */}
            <Card className="bg-white border-2 border-amber-200/50 shadow-sm mb-8 rounded-2xl">
              <CardContent className="p-8">
                <h2 className="font-serif text-2xl font-bold mb-6 text-earth-brown">Welcome to Women and the Way</h2>
                <div className="space-y-4 text-gray-700 leading-relaxed">
                  <p>
                    At Women and the Way, we walk gently with women through each phase of life. From the first bloom of menstruation to the golden glow of menopause, from postpartum tenderness to quiet restoration  -  our offerings are designed to honour the body's wisdom, support emotional wellness, and nourish the spirit.
                  </p>
                  <p>
                    We offer Chinese medicine, acupuncture, bodywork, in-home care, group healing, education, and curated products  -  all with a commitment to safety, respect, professionalism, and compliance with regulatory standards.
                  </p>
                  <p>
                    These Terms & Conditions reflect our values, your rights, and the care we take in delivering services that are both soulful and sound.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Terms Sections */}
            <div className="space-y-8">
              {/* 1. Practitioner Standards */}
              <Card className="bg-white border-2 border-amber-200/50 shadow-sm rounded-2xl">
                <CardContent className="p-8">
                  <h3 className="font-serif text-xl font-bold mb-4 flex items-center text-earth-brown">
                    <span className="text-2xl mr-3">🏥</span>
                    1. Practitioner Standards
                  </h3>
                  <p className="text-gray-700 mb-4">
                    All treatments and advice are delivered by qualified practitioners registered with the Chinese Medicine Board of Australia (CMBA) under the regulation of the Australian Health Practitioner Regulation Agency (AHPRA). We comply with:
                  </p>
                  <ul className="space-y-2 text-gray-700 ml-4">
                    <li className="flex items-start">
                      <span className="text-sage-green mr-2">•</span>
                      The National Code of Conduct for Health Care Workers (QLD)
                    </li>
                    <li className="flex items-start">
                      <span className="text-sage-green mr-2">•</span>
                      AHPRA's Advertising Guidelines
                    </li>
                    <li className="flex items-start">
                      <span className="text-sage-green mr-2">•</span>
                      Infection control and hygiene protocols
                    </li>
                  </ul>
                </CardContent>
              </Card>

              {/* 2. Informed Consent */}
              <Card className="bg-white border-2 border-amber-200/50 shadow-sm rounded-2xl">
                <CardContent className="p-8">
                  <h3 className="font-serif text-xl font-bold mb-4 flex items-center text-earth-brown">
                    <span className="text-2xl mr-3">🤝</span>
                    2. Informed Consent
                  </h3>
                  <p className="text-gray-700">
                    We take time to explain each treatment. Your participation is voluntary, and informed consent will be sought before any service begins. You are encouraged to ask questions and withdraw consent at any time.
                  </p>
                </CardContent>
              </Card>

              {/* 3. Holistic, Not Diagnostic */}
              <Card className="bg-white border-2 border-amber-200/50 shadow-sm rounded-2xl">
                <CardContent className="p-8">
                  <h3 className="font-serif text-xl font-bold mb-4 flex items-center text-earth-brown">
                    <span className="text-2xl mr-3">🌿</span>
                    3. Holistic, Not Diagnostic
                  </h3>
                  <p className="text-gray-700">
                    Our treatments are complementary, not a substitute for medical advice. We do not diagnose or cure. We support you holistically  -  body, mind, and spirit  -  and always recommend working alongside your medical team when needed.
                  </p>
                </CardContent>
              </Card>

              {/* 4. Results & Expectations */}
              <Card className="bg-white border-2 border-amber-200/50 shadow-sm rounded-2xl">
                <CardContent className="p-8">
                  <h3 className="font-serif text-xl font-bold mb-4 flex items-center text-earth-brown">
                    <span className="text-2xl mr-3">💫</span>
                    4. Results & Expectations
                  </h3>
                  <p className="text-gray-700">
                    We offer no guarantees of outcome. Healing is a partnership, and responses vary. We hold space for your body's innate capacity to restore balance. All language on our website and in our services reflects general wellness support, in line with AHPRA's guidelines.
                  </p>
                </CardContent>
              </Card>

              {/* 5. Privacy & Confidentiality */}
              <Card className="bg-white border-2 border-amber-200/50 shadow-sm rounded-2xl">
                <CardContent className="p-8">
                  <h3 className="font-serif text-xl font-bold mb-4 flex items-center text-earth-brown">
                    <span className="text-2xl mr-3">🔒</span>
                    5. Privacy & Confidentiality
                  </h3>
                  <p className="text-gray-700">
                    Your information is sacred. We adhere to the Australian Privacy Principles and maintain the strictest confidentiality across all services, both in-clinic and in-home.
                  </p>
                </CardContent>
              </Card>

              {/* 6. Payments, Packages & Cancellations */}
              <Card className="bg-white border-2 border-amber-200/50 shadow-sm rounded-2xl">
                <CardContent className="p-8">
                  <h3 className="font-serif text-xl font-bold mb-4 flex items-center text-earth-brown">
                    <span className="text-2xl mr-3">💰</span>
                    6. Payments, Packages & Cancellations
                  </h3>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start">
                      <span className="text-sage-green mr-2">•</span>
                      Full payment is due at time of booking or service
                    </li>
                    <li className="flex items-start">
                      <span className="text-sage-green mr-2">•</span>
                      Packages are non-refundable but transferable unless otherwise specified
                    </li>
                    <li className="flex items-start">
                      <span className="text-sage-green mr-2">•</span>
                      Cancellations require 24 hours' notice; less than this may incur a fee
                    </li>
                    <li className="flex items-start">
                      <span className="text-sage-green mr-2">•</span>
                      Late arrivals may shorten your treatment time
                    </li>
                  </ul>
                </CardContent>
              </Card>

              {/* 7. Products & Herbal Formulas */}
              <Card className="bg-white border-2 border-amber-200/50 shadow-sm rounded-2xl">
                <CardContent className="p-8">
                  <h3 className="font-serif text-xl font-bold mb-4 flex items-center text-earth-brown">
                    <span className="text-2xl mr-3">🌱</span>
                    7. Products & Herbal Formulas
                  </h3>
                  <p className="text-gray-700">
                    Any recommendations for herbs or supplements will be based on your individual needs and sourced from reputable suppliers. Instructions for safe use will be provided. These are not intended to replace prescribed medications.
                  </p>
                </CardContent>
              </Card>

              {/* 8. Community Offerings */}
              <Card className="bg-white border-2 border-amber-200/50 shadow-sm rounded-2xl">
                <CardContent className="p-8">
                  <h3 className="font-serif text-xl font-bold mb-4 flex items-center text-earth-brown">
                    <span className="text-2xl mr-3">👥</span>
                    8. Community Offerings
                  </h3>
                  <p className="text-gray-700">
                    Workshops and other group offerings are delivered with the same integrity and privacy expectations as individual care. Shared spaces foster connection but always honour personal boundaries.
                  </p>
                </CardContent>
              </Card>

              {/* 9. Feedback & Complaints */}
              <Card className="bg-white border-2 border-amber-200/50 shadow-sm rounded-2xl">
                <CardContent className="p-8">
                  <h3 className="font-serif text-xl font-bold mb-4 flex items-center text-earth-brown">
                    <span className="text-2xl mr-3">💬</span>
                    9. Feedback & Complaints
                  </h3>
                  <p className="text-gray-700">
                    We welcome feedback and hold a culture of continuous improvement. If you have concerns, please contact us directly. If needed, complaints may also be made via the Office of the Health Ombudsman (QLD) or AHPRA.
                  </p>
                </CardContent>
              </Card>

              {/* 10. Intellectual Property */}
              <Card className="bg-white border-2 border-amber-200/50 shadow-sm rounded-2xl">
                <CardContent className="p-8">
                  <h3 className="font-serif text-xl font-bold mb-4 flex items-center text-earth-brown">
                    <span className="text-2xl mr-3">📝</span>
                    10. Intellectual Property
                  </h3>
                  <p className="text-gray-700">
                    All content on our website and printed materials remains the property of Women and the Way and may not be reproduced without permission.
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Closing Section */}
            <Card className="bg-sage-800 text-white border-2 border-amber-200/50 shadow-sm mt-8 rounded-2xl">
              <CardContent className="p-8">
                <h3 className="font-serif text-2xl font-bold mb-6">In Closing</h3>
                <p className="text-sage-100 leading-relaxed mb-4">
                  These Terms are not just legal  -  they are a promise. A promise to walk with integrity, to serve with heart, and to offer every woman who enters our care a space where healing, rest, and reconnection are honoured.
                </p>
                <div className="text-center mt-6">
                  <p className="text-sage-200 font-medium">With respect and warmth,</p>
                  <p className="text-white font-serif text-lg font-semibold">Women and the Way</p>
                </div>
              </CardContent>
            </Card>

            {/* Call to Action */}
            <div className="text-center mt-12">
              <Button 
                size="lg" 
                className="bg-sage-600 hover:bg-sage-700 text-white px-8 py-4 text-lg font-semibold rounded-full"
                onClick={handleBooking}
              >
                Book Your Consultation
                <ArrowLeft className="w-5 h-5 ml-2 rotate-180" />
              </Button>
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default TermsConditionsPage; 